import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { agentProvidersApi } from '@/api/agentProviders'
import { useTenantKey } from './useTenantKey'

export function useAgentProviderModels() {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('agent-providers', 'models'),
    queryFn: agentProvidersApi.getModels,
    staleTime: Infinity,
  })
}

export function useAgentModelCatalog(provider: string) {
  const { data, isLoading } = useAgentProviderModels()

  return useMemo(() => {
    const entry = data?.find(p => p.provider === provider)
    const models = entry?.models ?? []
    const effortLabels: Record<string, string> = Object.fromEntries((entry?.efforts ?? []).map(e => [e.id, e.label]))
    const effortsByModel: Record<string, string[]> = Object.fromEntries(models.map(m => [m.id, m.efforts]))

    return {
      isLoading,
      models,
      defaultModel: entry?.defaultModel ?? undefined,
      defaultEffort: entry?.defaultEffort ?? undefined,
      effortIds: (entry?.efforts ?? []).map(e => e.id),
      effortsByModel,
      effortLabels,
      effortsFor: (model: string) => effortsByModel[model] ?? [],
      modelLabel: (id: string) => models.find(m => m.id === id)?.label ?? id,
      effortLabel: (id: string) => effortLabels[id] ?? id,
    }
  }, [data, isLoading, provider])
}

export function useAgentLabels() {
  const { data } = useAgentProviderModels()

  return useMemo(() => {
    const models = new Map((data ?? []).flatMap(p => p.models.map(m => [m.id, m.label] as const)))
    const efforts = new Map((data ?? []).flatMap(p => p.efforts.map(e => [e.id, e.label] as const)))
    return {
      modelLabel: (slug: string) => models.get(slug) ?? slug,
      effortLabel: (slug: string) => efforts.get(slug) ?? slug,
    }
  }, [data])
}
